import { isAnalyticsEligible } from './analyticsEligibility';
import { initGoogleTagManager } from './googleTagManager';

const MEASUREMENT_ID = 'G-S0FJZ9SE4Z';
const IDLE_TIMEOUT_MS = 1500;

let analyticsBootstrapped = false;
let analyticsBootstrapPromise = null;
let analyticsBootstrapScheduled = false;
let analyticsConsentGranted = false;
let vendorBootstrapPromise = null;
let vendorBootstrapped = false;
let queuedPageViews = [];
let queuedEvents = [];
let idleCallbackHandle = null;
let timeoutHandle = null;

const clearScheduledBootstrap = () => {
  if (typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function' && idleCallbackHandle !== null) {
    window.cancelIdleCallback(idleCallbackHandle);
  }
  if (timeoutHandle !== null) {
    clearTimeout(timeoutHandle);
  }
  idleCallbackHandle = null;
  timeoutHandle = null;
};

const normalizePagePath = (pagePath) => {
  if (pagePath) {
    return pagePath;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  return `${window.location.pathname}${window.location.search}`;
};

const sanitizeAnalyticsParams = (params = {}) => {
  const sanitizedParams = {};

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (trimmedValue) {
        sanitizedParams[key] = trimmedValue;
      }
      return;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      sanitizedParams[key] = value;
      return;
    }

    if (typeof value === 'boolean') {
      sanitizedParams[key] = value;
      return;
    }

    // GA4 ecommerce params (items) are arrays of flat objects. Everything inside them is
    // sanitized the same way, so the carve-out cannot smuggle nested junk into gtag.
    if (Array.isArray(value)) {
      const sanitizedEntries = value
        .filter((entry) => entry && typeof entry === 'object' && !Array.isArray(entry))
        .map((entry) => sanitizeAnalyticsParams(entry));

      if (sanitizedEntries.length) {
        sanitizedParams[key] = sanitizedEntries;
      }
    }
  });

  return sanitizedParams;
};

const buildPageLocation = (pagePath) => {
  if (typeof window === 'undefined' || !window.location?.origin || !pagePath?.startsWith('/')) {
    return null;
  }

  return `${window.location.origin}${pagePath}`;
};

const enqueuePageView = (pagePath) => {
  const normalizedPagePath = normalizePagePath(pagePath);
  if (!normalizedPagePath) {
    return;
  }

  if (queuedPageViews[queuedPageViews.length - 1]?.pagePath === normalizedPagePath) {
    return;
  }

  queuedPageViews.push({
    pagePath: normalizedPagePath,
    pageLocation: buildPageLocation(normalizedPagePath),
  });
};

const enqueueEvent = (eventName, params = {}) => {
  if (typeof eventName !== 'string' || !eventName.trim()) {
    return;
  }

  queuedEvents.push({
    eventName: eventName.trim(),
    params: sanitizeAnalyticsParams(params),
  });
};

const flushQueuedPageViews = () => {
  if (!analyticsBootstrapped || typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return false;
  }

  while (queuedPageViews.length > 0) {
    const nextPageView = queuedPageViews.shift();
    window.gtag(
      'event',
      'page_view',
      sanitizeAnalyticsParams({
        page_path: nextPageView.pagePath,
        page_location: nextPageView.pageLocation,
      }),
    );
  }

  return true;
};

const flushQueuedEvents = () => {
  if (!analyticsBootstrapped || typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return false;
  }

  while (queuedEvents.length > 0) {
    const nextEvent = queuedEvents.shift();
    window.gtag('event', nextEvent.eventName, nextEvent.params);
  }

  return true;
};

const flushQueuedAnalytics = () => {
  const pageViewsFlushed = flushQueuedPageViews();
  const eventsFlushed = flushQueuedEvents();
  return pageViewsFlushed || eventsFlushed;
};

const scheduleBootstrap = (callback) => {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    idleCallbackHandle = window.requestIdleCallback(
      () => {
        idleCallbackHandle = null;
        callback();
      },
      { timeout: IDLE_TIMEOUT_MS },
    );
    return;
  }

  timeoutHandle = setTimeout(() => {
    timeoutHandle = null;
    callback();
  }, 0);
};

const loadAnalyticsDependencies = async () => {
  return import('./analyticsBootstrap');
};

const updateAnalyticsConsent = () => {
  if (!analyticsBootstrapped || typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return false;
  }

  window.gtag('consent', 'update', {
    analytics_storage: analyticsConsentGranted ? 'granted' : 'denied',
  });

  return true;
};

const bootstrapAnalytics = async () => {
  if (analyticsBootstrapped) {
    return flushQueuedAnalytics();
  }

  if (analyticsBootstrapPromise) {
    await analyticsBootstrapPromise;
    return flushQueuedAnalytics();
  }

  analyticsBootstrapPromise = Promise.resolve().then(() => {
    initGoogleTagManager(MEASUREMENT_ID);
    analyticsBootstrapped = true;
    updateAnalyticsConsent();
  });

  try {
    await analyticsBootstrapPromise;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('Analytics bootstrap skipped:', error);
    }

    return false;
  } finally {
    analyticsBootstrapPromise = null;
  }

  return flushQueuedAnalytics();
};

const bootstrapAnalyticsVendors = () => {
  if (!isAnalyticsEligible() || !analyticsConsentGranted || vendorBootstrapped) {
    return true;
  }

  if (vendorBootstrapPromise) {
    return false;
  }

  vendorBootstrapPromise = (async () => {
    const { bootstrapAnalyticsVendors: bootstrapVendors } = await loadAnalyticsDependencies();
    await bootstrapVendors();
    vendorBootstrapped = true;
  })()
    .catch((error) => {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Analytics vendor bootstrap skipped:', error);
      }
      return false;
    })
    .finally(() => {
      vendorBootstrapPromise = null;
    });

  return false;
};

export const initAnalyticsServices = () => {
  if (!isAnalyticsEligible()) {
    return false;
  }

  if (!analyticsConsentGranted) {
    return false;
  }

  if (analyticsBootstrapped) {
    return flushQueuedAnalytics();
  }

  if (analyticsBootstrapScheduled || analyticsBootstrapPromise) {
    return false;
  }

  analyticsBootstrapScheduled = true;
  scheduleBootstrap(() => {
    analyticsBootstrapScheduled = false;
    void bootstrapAnalytics();
  });

  return false;
};

export const syncAnalyticsConsent = ({ granted } = {}) => {
  if (!granted || !isAnalyticsEligible()) {
    return false;
  }

  analyticsConsentGranted = true;
  initAnalyticsServices();
  updateAnalyticsConsent();
  return bootstrapAnalyticsVendors();
};

export const trackAnalyticsPageView = (pagePath) => {
  if (!isAnalyticsEligible()) {
    return false;
  }

  enqueuePageView(pagePath);

  if (!analyticsBootstrapped) {
    if (analyticsConsentGranted) {
      initAnalyticsServices();
    }
    return false;
  }

  if (typeof window.gtag !== 'function') {
    return false;
  }

  return flushQueuedPageViews();
};

export const trackAnalyticsEvent = (eventName, params = {}) => {
  if (!isAnalyticsEligible()) {
    return false;
  }

  enqueueEvent(eventName, params);

  if (!analyticsBootstrapped) {
    if (analyticsConsentGranted) {
      initAnalyticsServices();
    }
    return false;
  }

  if (typeof window.gtag !== 'function') {
    return false;
  }

  return flushQueuedEvents();
};

/**
 * GA4 only fills its Monetisation reports from the reserved `purchase` event, and only
 * when it carries transaction_id, value and currency — which is why the Sales section
 * stayed empty while `purchase_confirmed` was being sent instead. The amount comes from
 * Stripe, not from a constant, so promotion codes and price changes stay correct.
 */
export const trackAnalyticsPurchase = ({ transactionId, amountRappen, currency, itemName }) => {
  const value = Number.isFinite(amountRappen) ? amountRappen / 100 : null;

  return trackAnalyticsEvent('purchase', {
    transaction_id: transactionId,
    value,
    currency,
    items: [
      {
        item_id: 'full_access',
        item_name: itemName || 'Full Access',
        price: value,
        quantity: 1,
      },
    ],
  });
};

export const __resetAnalyticsServicesForTests = () => {
  analyticsBootstrapped = false;
  analyticsBootstrapPromise = null;
  analyticsBootstrapScheduled = false;
  analyticsConsentGranted = false;
  vendorBootstrapPromise = null;
  vendorBootstrapped = false;
  queuedPageViews = [];
  queuedEvents = [];
  clearScheduledBootstrap();
};
